import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Language, LocalizedText } from '@/shared/api';
import { getUiLanguage } from '@/shared/config';
import { SegmentedControl } from '../SegmentedControl';
import { useContentLanguages } from './contentLanguages';
import { localizedTextOf } from './localizedText';

export function isLanguageOf(languages: readonly Language[], value: string): value is Language {
  return languages.some((language) => language === value);
}

/** Язык ввода по умолчанию: исходный язык текста, иначе язык интерфейса (D13), иначе первый. */
function initialLanguage(value: LocalizedText | null, languages: readonly Language[]): Language {
  const source = value?.source_lang;
  if (source && languages.includes(source)) return source;
  const ui = getUiLanguage();
  if (isLanguageOf(languages, ui)) return ui;
  return languages[0] ?? 'ru';
}

/** Ошибка вложенного пути (`поле.en`) из дерева ошибок RHF без приведения типов. */
export function nestedErrorMessage(error: unknown, key: string): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const nested = Object.entries(error).find(([entryKey]) => entryKey === key)?.[1];
  if (typeof nested !== 'object' || nested === null || !('message' in nested)) return undefined;
  return typeof nested.message === 'string' ? nested.message : undefined;
}

/**
 * Состояние ввода LocalizedText (COM-09) для поля формы и правки на месте: языки рынка, язык ввода,
 * текст на нём, изменение (метаданные перевода сохраняются в значении для показа), строка статуса
 * перевода (World) и сообщение об ошибке с меткой языка.
 */
export function useLocalizedInput(
  value: LocalizedText | null,
  onChange: (next: LocalizedText) => void,
) {
  const { t } = useTranslation('form');
  const { languages, autoTranslate } = useContentLanguages();
  const [active, setActive] = useState<Language>(() => initialLanguage(value, languages));
  const language = languages.includes(active) ? active : (languages[0] ?? active);
  const multilingual = languages.length > 1;
  const text = localizedTextOf(value, language);

  function change(nextText: string) {
    const next: LocalizedText = {};
    if (multilingual) {
      // Метаданные перевода остаются в значении для показа; в запрос их не отправляют.
      if (value?.source_lang) next.source_lang = value.source_lang;
      if (value?.translation_status) next.translation_status = value.translation_status;
    }
    for (const code of languages) next[code] = localizedTextOf(value, code);
    next[language] = nextText;
    onChange(next);
  }

  function statusLine(): string | null {
    if (!multilingual) return null;
    const source = value?.source_lang;
    if (source && languages.includes(source)) {
      const parts = [t('localized.source', { lang: t(`localized.short.${source}`) })];
      const translation = value?.translation_status;
      if (translation) parts.push(t(`localized.status.${translation}`));
      return parts.join(' · ');
    }
    if (!autoTranslate) return null;
    const others = languages.filter((code) => code !== language);
    if (others.length === 0) return null;
    return t('localized.autoHint', {
      lang: others.map((code) => t(`localized.short.${code}`)).join(', '),
    });
  }

  /** Сообщение об ошибке: корневое или по языкам (`EN: …`). */
  function errorMessage(error: unknown): string | undefined {
    const root =
      typeof error === 'object' && error !== null && 'message' in error ? error.message : undefined;
    if (typeof root === 'string' && root) return root;
    const parts = languages.flatMap((code) => {
      const message = nestedErrorMessage(error, code);
      if (!message) return [];
      return [
        multilingual
          ? t('localized.languageError', { lang: t(`localized.short.${code}`), message })
          : message,
      ];
    });
    return parts.length > 0 ? parts.join(' ') : undefined;
  }

  const switcher = multilingual ? (
    <SegmentedControl
      variant="compact"
      aria-label={t('localized.inputLanguage')}
      value={language}
      onValueChange={(next) => {
        if (isLanguageOf(languages, next)) setActive(next);
      }}
      items={languages.map((code) => ({ value: code, label: t(`localized.short.${code}`) }))}
    />
  ) : null;

  return {
    languages,
    language,
    multilingual,
    text,
    change,
    status: statusLine(),
    errorMessage,
    switcher,
  };
}
